'use client'

import * as React from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { X } from 'lucide-react'

interface StockDetailDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    symbol: string
    exchange?: string
}

export function StockDetailDialog({
    open,
    onOpenChange,
    symbol,
    exchange = 'NASDAQ',
}: StockDetailDialogProps) {
    const iframeRef = React.useRef<HTMLIFrameElement>(null)
    const [iframeHeight, setIframeHeight] = React.useState<number | 'auto'>(800) // Default height

    // Create the slug format: exchange-symbol or just symbol if NASDAQ
    const slug = React.useMemo(() => {
        if (exchange && exchange.toUpperCase() !== 'NASDAQ') {
            return `${exchange.toLowerCase()}-${symbol.toLowerCase()}`
        }
        return symbol.toLowerCase()
    }, [symbol, exchange])

    const stockPageUrl = `/stock/${slug}#embedded`

    // Listen for height messages from iframe
    React.useEffect(() => {
        if (!open) return

        const handleMessage = (event: MessageEvent) => {
            // In production, you should verify event.origin for security
            // For now, we'll accept messages from any origin since it's same-origin
            if (event.data?.type === 'iframe-resize' && typeof event.data.height === 'number') {
                setIframeHeight(event.data.height)
            }
        }

        window.addEventListener('message', handleMessage)

        return () => {
            window.removeEventListener('message', handleMessage)
        }
    }, [open])

    // Reset height when dialog opens/closes or URL changes
    React.useEffect(() => {
        if (open) {
            setIframeHeight(800) // Reset to default when opening
        }
    }, [open, stockPageUrl])

    const dialogClassName =
        'max-w-[100vw]! w-full max-h-screen h-screen pt-0 pb-8 px-20 bg-transparent border-0 gap-0 rounded-none overflow-auto'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className={dialogClassName}
                overlayClassName="backdrop-blur-sm"
                showCloseButton={false}
            >
                <DialogHeader className="sr-only">
                    <DialogTitle>{symbol} Stock Details</DialogTitle>
                </DialogHeader>

                {/* Custom control buttons - sticky inside iframe area */}
                <div
                    className="sticky top-[3rem] z-50 flex justify-end pointer-events-none"
                    style={{ marginRight: '1rem' }}
                >
                    <div className="pointer-events-auto">
                        <Button
                            size="icon"
                            className="h-8 w-8 bg-black/3 dark:bg-white/3"
                            onClick={() => onOpenChange(false)}
                        >
                            <X className="h-4 w-4 text-black/40 dark:text-white/40" />
                            <span className="sr-only">Close</span>
                        </Button>
                    </div>
                </div>

                <div className="relative w-full">
                    <iframe
                        ref={iframeRef}
                        key={stockPageUrl}
                        src={stockPageUrl}
                        className="w-full pt-10 px-2.5 border-0 rounded-4xl bg-white dark:bg-black"
                        style={{
                            height:
                                typeof iframeHeight === 'number'
                                    ? `${iframeHeight}px`
                                    : iframeHeight,
                            minHeight: '800px',
                        }}
                        scrolling="no"
                        title={`${symbol} Stock Details`}
                    />
                </div>
            </DialogContent>
        </Dialog>
    )
}
